import { FC, useMemo, useState, forwardRef, useRef, useImperativeHandle, MutableRefObject, useEffect } from 'react';
import { type IconName } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames/bind';
import { FAIcon } from '../Icons/Icons';
import { EVENT_KEY, MLPVersion } from '../../types/enums';
import { useTranslation } from '../Translation/TranslationProvider';
import { QuantityStepperPropType } from './types/propTypes';
import { QuantityStepperType, QuantityStepperSize, QuantityStepperStatus } from './types/enums';
import styles from './QuantityStepper.module.scss';

const cx = classNames.bind(styles);

const currentStatusIcons = (status: QuantityStepperStatus | undefined, message: string): IconName | null => {
  if (message) {
    if (status === QuantityStepperStatus.VALID) {
      return 'circle-check';
    } else if (status === QuantityStepperStatus.INVALID) {
      return 'square-exclamation';
    }
  }
  return null;
};
const getProcessValue = (value: string, maxCount: number, prefix: string | undefined): string => {
  let currentVal = value;
  if (prefix) {
    const [_, val] = currentVal.split(prefix);
    currentVal = Number.isNaN(val) ? '' : val;
  }
  return parseInt(currentVal) > maxCount ? maxCount.toString() : currentVal;
};
const getInputValue = (
  prefix: string | undefined,
  isFocused: boolean,
  quantity: number,
  minCount: number,
  mlpVersion: MLPVersion,
): string => {
  let value = quantity.toString();
  if (mlpVersion === MLPVersion.TWO && prefix) {
    if (Number.isNaN(quantity)) {
      value = isFocused ? prefix : '';
    } else {
      value = prefix + value;
    }
  } else {
    if (Number.isNaN(quantity)) {
      value = isFocused ? '' : minCount.toString();
    }
  }
  return value;
};
const QuantityStepper: FC<QuantityStepperPropType> = forwardRef<HTMLInputElement, QuantityStepperPropType>(
  (
    {
      id = 'quantity',
      name = 'quantity',
      prefix,
      count = 0,
      minCount = 0,
      maxCount = 999,
      type = QuantityStepperType.OUTLINED,
      size = QuantityStepperSize.SMALL,
      disabled = false,
      message = '',
      disableMinusButton = false,
      disablePlusButton = false,
      inputChangeHandler = () => 0,
      minusClickHandler = () => 0,
      plusClickHandler = () => 0,
      status = QuantityStepperStatus.DEFAULT,
      ariaLabelledMinus,
      ariaLabelledPlus,
      mlpVersion = MLPVersion.ONE,
      step = 1,
      ...restProps
    },
    ref,
  ) => {
    const t = useTranslation();
    const [quantity, setQuantity] = useState(count);
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef() as MutableRefObject<HTMLInputElement>;
    const statusIcon = useMemo(() => currentStatusIcons(status, message), [status, message]);

    const ariaLabelledMinusValue = ariaLabelledMinus || t('nextgen.QuantityStepperMinus.ariaLabel');
    const ariaLabelledPlusValue = ariaLabelledPlus || t('nextgen.QuantityStepperPlus.ariaLabel');

    useImperativeHandle(ref, () => inputRef.current);

    const onMinusEvent = () => {
      if (!disabled && !disableMinusButton && quantity > minCount) {
        setQuantity(quantity - step);
        minusClickHandler?.(quantity - step);
      }
    };
    const onPlusEvent = () => {
      if (!disabled && !disablePlusButton && quantity < maxCount) {
        setQuantity((quantity || minCount) + step);
        plusClickHandler?.(quantity + step);
      }
    };
    useEffect(() => {
      setQuantity(count);
    }, [count]);
    const CClassname = cx(
      `quantity-stepper-${mlpVersion}`,
      { 'focused-element': isFocused },
      ...(mlpVersion === MLPVersion.ONE ? [type, size] : ''),
      { disabled: disabled },
      [status],
    );
    const CMinusBtnClassname = cx('minus', { inactive: quantity === minCount || disabled || disableMinusButton });
    const CPlusBtnClassname = cx('plus', { inactive: quantity === maxCount || disabled || disablePlusButton });
    return (
      <>
        <div className={CClassname} data-testid="quantity-stepper">
          <div
            role="button"
            aria-label={ariaLabelledMinusValue}
            className={CMinusBtnClassname}
            tabIndex={-2}
            data-testid="minus"
            onClick={onMinusEvent}
            onKeyDown={(e) => {
              if (e.key === EVENT_KEY.ENTER) {
                onMinusEvent();
              }
            }}
          >
            <FAIcon iconName={'minus'} className={styles['icon']} />
          </div>
          <button
            className={styles['quantity']}
            onClick={() => inputRef?.current?.focus()}
            data-testid="quantity-wrapper"
          >
            <input
              type="text"
              data-testid="quantity-value"
              ref={inputRef}
              name={name}
              min={minCount}
              max={maxCount}
              id={id}
              disabled={disabled}
              value={getInputValue(prefix, isFocused, quantity, minCount, mlpVersion)}
              {...restProps}
              onFocus={(e) => {
                restProps?.onFocus?.(e);
                setIsFocused(true);
              }}
              onBlur={(e) => {
                restProps?.onBlur?.(e);
                setIsFocused(false);
              }}
              onChange={(e) => {
                if (!disabled) {
                  const value = getProcessValue(e.target.value, maxCount, prefix);
                  setQuantity(parseInt(value));
                  inputChangeHandler?.(parseInt(value) || minCount);
                }
              }}
              autoComplete="false"
            />
          </button>
          <div
            role="button"
            aria-label={ariaLabelledPlusValue}
            className={CPlusBtnClassname}
            tabIndex={-1}
            data-testid="plus"
            onClick={onPlusEvent}
            onKeyDown={(e) => {
              if (e.key === EVENT_KEY.ENTER) {
                onPlusEvent();
              }
            }}
          >
            <FAIcon iconName={'plus'} className={styles['icon']} />
          </div>
        </div>
        <div className={styles['quantity-stepper-message']}>
          {statusIcon && <FAIcon iconName={statusIcon} />}
          <span>{message}</span>
        </div>
      </>
    );
  },
);
QuantityStepper.displayName = 'QuantityStepper';
export default QuantityStepper;
export { QuantityStepperType, QuantityStepperSize, QuantityStepperStatus };
